import { useCallback, useEffect, useMemo, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import { AbortError } from '@app/errors/AbortError'

import { TelegramSDK } from '@app/utils/TelegramSDK'

import { asError } from '@app/packages/asError/asError'
import { createUseContext } from '@app/packages/componentContext/createUseContext'
import { useThunkSelector } from '@app/packages/thunkSelector/useThunkSelector'
import type { YandexOauthResult } from '@app/packages/yandexId/runYandexOauthPopup'
import { runYandexOauthPopup, YandexPopupBlockedError, YandexPopupClosedError } from '@app/packages/yandexId/runYandexOauthPopup'

import { telegramBotCredentialsSelector } from '@app/store/selectors/telegram'
import { yandexConfigResultSelector } from '@app/store/slices/yandex_config'

export type TelegramResult = { bot: string; auth_data: Telegram.AuthData }
export type TelegramOnAuthenticateCallback = (state: TelegramResult) => unknown

export const useTelegramLogin = () => {
  const tgBot = useSelector(telegramBotCredentialsSelector)
  const [error, setError] = useState<Error | null>()

  useEffect(() => {
    // preload SDK so clicking on add button will become sync
    // (required by browser to open new window and not block it)
    TelegramSDK.init().catch(e => {
      setError(e)
    })
  }, [])

  return useCallback(async () => {
    if (!tgBot) throw new Error("Telegram credentials weren't provided")
    const sdk = new TelegramSDK(tgBot.id, tgBot.name)
    if (error) throw error
    return await sdk.login()
  }, [error, tgBot])
}

/** Идентификатор клиента + запуск OAuth-попапа. Подменяется в полигонах через useYandexLoginAdapter.setContext. */
export type YandexLoginAdapter = {
  clientId: string | null
  run: (clientId: string) => Promise<YandexOauthResult>
}

export const useYandexLoginAdapter = createUseContext((): YandexLoginAdapter => {
  const clientId = useThunkSelector(yandexConfigResultSelector).suspend()?.client_id ?? null
  return useMemo(() => ({ clientId, run: id => runYandexOauthPopup({ clientId: id }) }), [clientId])
})

export const useYandexLogin = () => {
  const { formatMessage } = useIntl()
  const { clientId, run } = useYandexLoginAdapter()

  const login = useMemo(() => {
    if (!clientId) return null
    return async (): Promise<YandexOauthResult> => {
      try {
        return await run(clientId)
      } catch (error) {
        if (error instanceof YandexPopupClosedError) throw new AbortError('Yandex login was aborted')
        if (error instanceof YandexPopupBlockedError) throw new Error(formatMessage(messages.error_yandex_popup), { cause: error })
        throw asError(error)
      }
    }
  }, [run, clientId, formatMessage])

  return login
}

const messages = defineMessages({
  error_yandex_popup: { id: "hooks.useSocialLogin.messages.error_yandex_popup.be96", defaultMessage: 'Браузер заблокировал окно входа через Яндекс — разрешите всплывающие окна и попробуйте ещё раз' },
})
